<template>
  <div class="histogram-chart-wrapper">
    <div :id="chartId" class="histogram-chart" v-observe-visibility="visibilityChange" style="height: 100%; margin-left: -13px;"></div>
    <div :id="`hover-info-${chartId}`" class="mobile-hover-info">
      <div class="info-wrapper" v-if="info.seats">
        <div class="info-label">Seats</div>
        <div class="info-value">
          <span style="color: #FA3138;">{{ info.seats }}R </span> /
          <span style="color: #66ABCF">{{ (majority * 2) - info.seats }}D </span>
        </div>
      </div>
      <div class="mobile-info-wrapper">
        <div class="mobile-info-label">Simulations</div>
        <div class="mobile-info-value">{{ info.simulations }}</div>
      </div>
      <div class="mobile-info-wrapper">
        <div class="mobile-info-label">% of Simulations</div>
        <div class="mobile-info-value">{{ info.percentage }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as d3 from 'd3'

const props = {
  simulations: {
    type: Array,
    required: true
  },
  chamber: {
    type: String,
    required: true
  },
  majority: {
    type: Number,
    required: true
  },
  gopProb: {
    type: String,
    required: false
  },
  movement: {
    type: Object,
    required: false
  }
}

const computed = {
  ...mapGetters({
    screenWidth: 'ux/screenWidth',
    currentDate: 'aggregateModels/currentDate'
  }),
  chartId () {
    return `${this.chamber}-histogram-chart`
  }
}

const methods = {
  drawChart () {
    this.height = 650

    d3.select(`#hover-info-${this.chartId}`).style('opacity', 0)

    const chart = d3.select(`#${this.chartId}`)
    this.width = chart._groups[0][0].clientWidth

    chart.selectAll('*').remove()

    this.svg = chart.append('svg')
      .attr('width', this.width)
      .attr('height', this.height)

    this.sims = this.simulations.filter(item => item.value > 500)

    const y = d3.scaleBand()
      .domain(this.sims.map(d => d.seats))
      .range([this.margin.bottom, this.height - this.margin.top])
      .padding(0.1)
    const x = d3.scaleLinear()
      .domain([0, d3.max(this.sims, d => d.value)])
      .range([this.margin.right, this.width - this.margin.left])

    // let tickValues = null

    // if (this.chamber === 'house') {
    //   tickValues = this.sims.map(item => item.seats)
    //     .filter(item => item % 10 === 0)
    // } else {
    //   tickValues = this.sims.map(item => item.seats)
    //     .filter(item => item % 2 === 0)
    // }

    const rects = this.svg.append('g')
      .selectAll('rect')
      .data(this.sims)
      .enter().append('rect')
      .attr('fill', (d) => d.seats >= this.majority ? '#FA3138' : '#66ABCF')
      .attr('width', (d) => x(d.value))
      .attr('transform', `translate(0, -${this.margin.top})`)

    const overlayRects = this.svg.append('g')
      .selectAll('rect')
      .data(this.sims)
      .enter().append('rect')
      .attr('fill', 'rgb(189, 189, 189)')
      .style('opacity', 0)
      .attr('width', '100%')
      .attr('transform', `translate(0, -${this.margin.top})`)

    const self = this

    overlayRects
      .on('mousemove', function (d) {
        d3.select(`#hover-info-${self.chartId}`).style('opacity', 1)
        d3.select(this).style('opacity', 0.2)
        self.info.seats = d.seats
        self.info.simulations = d.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        self.info.percentage = ((d.value / 14000000) * 100).toFixed(2)
      })
      .on('mouseout', function (d) {
        d3.select(this).style('opacity', 0)
        d3.select(`#hover-info-${this.chartId}`).style('opacity', 0)
      })
      .attr('y', d => y(d.seats))
      .attr('height', 0)
      .transition()
      .delay((d, i) => this.chamber === 'senate' ? i * 100 : i * 20)
      .attr('height', d => y.bandwidth() - 1)

    rects
      .attr('y', d => y(d.seats))
      .attr('height', 0)
      .transition()
      .delay((d, i) => this.chamber === 'senate' ? i * 100 : i * 20)
      .attr('height', d => y.bandwidth() - 1)

    const maxValue = d3.max(this.sims, d => d.value)
    const maxSeats = this.sims.find(item => item.value === maxValue).seats

    // y-axis
    this.svg.append('line')
      .style('stroke-width', 1.5)
      .style('stroke', '#555')
      .style('fill', 'none')
      .attr('x1', x(this.majority))
      .attr('y1', 0)
      .attr('x2', x(this.majority))
      .attr('y2', this.height)

    // majority line
    this.svg.append('line')
      .style('stroke-width', 1.7)
      .style('stroke', '#555')
      .style('fill', 'none')
      .attr('x1', 0)
      .attr('y1', y(this.majority) - this.margin.top)
      .attr('x2', this.width)
      .attr('y2', y(this.majority) - this.margin.top)

    // majority text
    this.svg.append('text')
      .attr('fill', '#333')
      .attr('font-size', '0.75em')
      .attr('font-weight', 500)
      .attr('text-anchor', 'end')
      .style('opacity', 1)
      .style('letter-spacing', '0.07em')
      .text(() => `MAJORITY (${this.majority})`)
      .attr('x', this.width - 5)
      .attr('y', () => {
        if (this.majority < maxSeats) {
          return y(this.majority) - 16
        } else {
          return y(this.majority) + 5
        }
      })

    // gop win probability
    this.svg.append('text')
      .attr('fill', '#333')
      .attr('font-size', '0.9em')
      .attr('font-weight', 500)
      .attr('text-anchor', 'end')
      .style('pointer-events', 'none')
      .style('opacity', 1)
      .style('letter-spacing', '0.07em')
      .text(() => 'GOP Win Probability')
      .attr('x', this.width - 10)
      .attr('y', 40)

    this.svg.append('text')
      .attr('fill', '#333')
      .attr('font-size', '1.2em')
      .attr('font-weight', 600)
      .attr('text-anchor', 'end')
      .style('pointer-events', 'none')
      .style('opacity', 1)
      .style('letter-spacing', '0.05em')
      .text(() => this.gopProb)
      .attr('x', this.width - 10)
      .attr('y', 65)

    // movement since last model
    this.svg.append('text')
      .attr('fill', '#333')
      .attr('font-size', '0.9em')
      .attr('font-weight', 500)
      .attr('text-anchor', 'end')
      .style('pointer-events', 'none')
      .style('opacity', 1)
      .style('letter-spacing', '0.07em')
      .text(() => 'Movement Since Last Model')
      .attr('x', this.width - 10)
      .attr('y', 120)

    this.svg.append('text')
      .attr('fill', () => {
        if (this.movement.party === 'R') {
          return '#FA3138'
        } else {
          return '#66ABCF'
        }
      })
      .attr('font-size', '1.2em')
      .attr('font-weight', 600)
      .attr('text-anchor', 'end')
      .style('pointer-events', 'none')
      .style('opacity', 1)
      .style('letter-spacing', '0.05em')
      .text(() => `+${this.movement.diff}% ${this.movement.party}`)
      .attr('x', this.width - 10)
      .attr('y', 150)
  },
  visibilityChange (isVisible) {
    if (isVisible && !this.render) {
      this.render = true
    }
  }
}

export default {
  props,
  computed,
  methods,
  data () {
    return {
      width: null,
      height: null,
      margin: { top: 10, right: 0, bottom: 10, left: 0 },
      svg: null,
      render: false,
      sims: null,
      info: {
        seats: null,
        simulations: null,
        percentage: null
      }
    }
  },
  watch: {
    screenWidth () {
      if (this.render) {
        if (this.render && typeof this.simulations[0] === 'object') {
          this.drawChart()
        }
      }
    },
    currentDate () {
      if (this.render && typeof this.simulations[0] === 'object') {
        this.drawChart()
      }
    },
    render () {
      if (this.render && typeof this.simulations[0] === 'object') {
        this.drawChart()
      }
    }
  }
}
</script>

<style>
.histogram-chart-wrapper {
  position: relative;
}

.mobile-hover-info {
  position: absolute;
  top: 460px;
  right: 10px;
  pointer-events: none;
}

.mobile-info-wrapper {
  text-align: end;
  margin-bottom: 10px;
}

.mobile-info-label {
  color: #333;
  font-size: 0.9em;
  font-weight: 500;
  letter-spacing: 0.07em;
}

.mobile-info-value {
  color: #333;
  font-size: 1.2em;
  font-weight: 600;
  letter-spacing: 0.05em;
}

.histogram-chart {
  backface-visibility: hidden;
}

.histogram-chart .x-axis path {
  display: none;
}

.histogram-chart .tick line {
  display: none;
}

.histogram-chart .x-axis .tick text {
  font-size: 1.2em;
  font-weight: 300;
  letter-spacing: 0.08em;
}
</style>



// WEBPACK FOOTER //
// src/components/charts/HorizontalHistogram.vue